import React, { useMemo, useRef, useState } from 'react';
import { Granularity } from '../../../services/eventLog.service';

type Point = { date: string; total: number };

type Props = {
  data: Point[];
  height?: number;
  color?: string;
  showAxis?: boolean;
  granularity?: Granularity;
};

/**
 * Inline SVG line chart with hover tooltip.
 * No external charting dep. Renders a soft area + crisp line, plus a vertical guide
 * and a tooltip on mouse-over.
 */
const LineChart: React.FC<Props> = ({
  data,
  height = 160,
  color = '#6366f1',
  showAxis = true,
  granularity = 'daily',
}) => {
  const [hover, setHover] = useState<{ idx: number; x: number; y: number } | null>(null);
  const [width, setWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(Math.max(200, Math.floor(w)));
    });
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  const padding = {
    top: 10,
    right: showAxis ? 52 : 10,
    bottom: showAxis ? 22 : 6,
    left: showAxis ? 32 : 6,
  };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const { points, maxVal, linePath, areaPath, avgVal, avgY } = useMemo(() => {
    if (!data.length) {
      return { points: [], maxVal: 0, linePath: '', areaPath: '', avgVal: 0, avgY: 0 };
    }
    const m = Math.max(...data.map(d => d.total), 1);
    const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
    const pts = data.map((d, i) => {
      const x = padding.left + i * stepX;
      const y = padding.top + innerH - (d.total / m) * innerH;

      return { x, y, value: d.total, date: d.date };
    });
    const lp = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
    const lastX = pts[pts.length - 1].x;
    const firstX = pts[0].x;
    const ap = `${lp} L ${lastX} ${padding.top + innerH} L ${firstX} ${padding.top + innerH} Z`;

    const avgVal = data.reduce((s, d) => s + d.total, 0) / data.length;
    const avgY = padding.top + innerH - (avgVal / m) * innerH;

    return { points: pts, maxVal: m, linePath: lp, areaPath: ap, avgVal, avgY };
  }, [data, innerW, innerH, padding.left, padding.top]);

  const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!points.length) return;
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    let nearest = 0;
    let nearestDist = Infinity;
    for (let i = 0; i < points.length; i++) {
      const d = Math.abs(points[i].x - mouseX);
      if (d < nearestDist) {
        nearest = i;
        nearestDist = d;
      }
    }
    setHover({ idx: nearest, x: points[nearest].x, y: points[nearest].y });
  };

  const formatDateShort = (iso: string) => {
    const d = new Date(iso + 'T00:00:00Z');

    if (granularity === 'monthly') {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
    }

    if (granularity === 'weekly') {
      return `Wk ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    }

    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  if (!data.length) {
    return (
      <div ref={containerRef} style={{ height }} className="flex items-center justify-center">
        <p className="text-sm text-gray-400">No activity in this range</p>
      </div>
    );
  }

  // Y-axis ticks (3 levels: max, mid, 0)
  const yTicks = [0, Math.round(maxVal / 2), maxVal];

  // Sparse x-axis labels — first, middle(ish), last
  const xLabelIndexes =
    data.length <= 2 ? data.map((_, i) => i) : [0, Math.floor(data.length / 2), data.length - 1];

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <svg
        width={width}
        height={height}
        onMouseMove={onMouseMove}
        onMouseLeave={() => setHover(null)}
        style={{ display: 'block' }}
      >
        {showAxis &&
          yTicks.map((t, i) => {
            const y = padding.top + innerH - (t / Math.max(maxVal, 1)) * innerH;

            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  x2={padding.left + innerW}
                  y1={y}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeDasharray="2,3"
                />
                <text x={padding.left - 6} y={y + 3} fontSize="10" textAnchor="end" fill="#9ca3af">
                  {t}
                </text>
              </g>
            );
          })}

        {/* Average line */}
        <line
          x1={padding.left}
          x2={padding.left + innerW}
          y1={avgY}
          y2={avgY}
          stroke="#f59e0b"
          strokeWidth={1}
          strokeDasharray="6,4"
        />
        <text
          x={padding.left + innerW + 4}
          y={avgY + 3}
          fontSize="9"
          fill="#f59e0b"
          fontWeight="600"
        >
          avg {Math.round(avgVal)}
        </text>

        <path d={areaPath} fill={color} fillOpacity={0.12} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} />

        {showAxis &&
          xLabelIndexes.map(i => (
            <text
              key={i}
              x={points[i].x}
              y={height - 4}
              fontSize="10"
              textAnchor="middle"
              fill="#9ca3af"
            >
              {formatDateShort(data[i].date)}
            </text>
          ))}

        {hover && (
          <>
            <line
              x1={hover.x}
              x2={hover.x}
              y1={padding.top}
              y2={padding.top + innerH}
              stroke="#9ca3af"
              strokeDasharray="2,3"
            />
            <circle cx={hover.x} cy={hover.y} r={4} fill={color} stroke="#fff" strokeWidth={2} />
          </>
        )}
      </svg>

      {hover && (
        <div
          className="absolute pointer-events-none rounded-md bg-gray-900 px-2 py-1 text-xs text-white shadow-lg whitespace-nowrap"
          style={{
            left: hover.x,
            top: hover.y - 8,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="font-medium">{points[hover.idx]?.value.toLocaleString()} events</div>
          <div className="text-gray-300 text-[10px]">
            {formatDateShort(data[hover.idx]?.date || '')}
          </div>
        </div>
      )}
    </div>
  );
};

export default LineChart;
